import { useEffect, useRef } from "react"
import { registerHandler } from "./handler-stack"
import { BackPriority, type BackPriorityType } from "./priorities"

export type UseBackButtonOptions = {
	/**
	 * Слой, на котором работает обработчик. По умолчанию `BackPriority.PAGE`.
	 * Для попапов/оверлеев — `BackPriority.POPUP`, чтобы back сначала закрывал
	 * оверлей, а не уводил со страницы.
	 */
	priority?: BackPriorityType
}

/**
 * Universal back button hook. Registers a handler in the global priority stack.
 * Platform-specific behavior (Telegram native button, visual overlay) is handled
 * by BackButtonManager mounted in the root layout.
 *
 * @param onClick - Callback to run on back. `undefined` снимает обработчик со стека —
 *   для тех, кто остаётся смонтированным в невидимом состоянии (оверлеи вне очереди,
 *   exit-анимация): иначе «назад» ушла бы скрытому попапу вместо страницы.
 * @param options - Optional config. Use `{ priority: BackPriority.POPUP }` for overlays.
 */
export function useBackButton(
	onClick: (() => void) | undefined,
	options?: UseBackButtonOptions,
) {
	const onClickRef = useRef(onClick)
	const priorityRef = useRef<BackPriorityType>(
		options?.priority ?? BackPriority.PAGE,
	)
	const active = onClick != null

	useEffect(() => {
		onClickRef.current = onClick
	}, [onClick])

	useEffect(() => {
		if (!active) return
		return registerHandler(() => onClickRef.current?.(), priorityRef.current)
	}, [active])
}
